<template>
	<div v-if="dialog" class="unsaved-dialog" role="dialog" aria-modal="true">
		<div class="unsaved-dialog__backdrop" @click="handleCancel"></div>
		<div class="unsaved-dialog__card">
			<div class="unsaved-dialog__title">
				<span class="unsaved-dialog__title-icon" aria-hidden="true">!</span>
				<span>Несохранённые изменения</span>
			</div>

			<div class="unsaved-dialog__content">
				<p class="unsaved-dialog__message">{{ message }}</p>
			</div>

			<div class="unsaved-dialog__actions">
				<button type="button" class="unsaved-dialog__btn unsaved-dialog__btn--ghost" @click="handleCancel">
					Отмена
				</button>
				<button
					v-if="showSaveButton"
					type="button"
					class="unsaved-dialog__btn unsaved-dialog__btn--primary"
					:disabled="saving"
					@click="handleSave"
				>
					{{ saving ? 'Сохранение...' : 'Сохранить' }}
				</button>
				<button type="button" class="unsaved-dialog__btn unsaved-dialog__btn--danger" @click="handleDiscard">
					Не сохранять
				</button>
			</div>
		</div>
	</div>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue'

const props = defineProps({
	message: {
		type: String,
		default: 'У вас есть несохранённые изменения. Что вы хотите сделать?',
	},
	autoSaveAvailable: {
		type: Boolean,
		default: false,
	},
	showSaveButton: {
		type: Boolean,
		default: true,
	},
	saveAction: {
		type: Function,
		default: null,
	},
})

const emit = defineEmits(['save', 'discard', 'cancel'])

const dialog = ref(false)
const saving = ref(false)
const resolveAction = ref(null)

const open = () => {
	dialog.value = true
	return new Promise((resolve) => {
		resolveAction.value = resolve
	})
}

const closeDialog = (result) => {
	dialog.value = false
	saving.value = false
	if (resolveAction.value) {
		resolveAction.value(result)
		resolveAction.value = null
	}
}

const handleDiscard = () => {
	emit('discard')
	closeDialog('discard')
}

const handleCancel = () => {
	emit('cancel')
	closeDialog('cancel')
}

const handleSave = async () => {
	if (saving.value) return
	saving.value = true
	try {
		if (props.saveAction) {
			await props.saveAction()
		} else {
			emit('save')
		}
		closeDialog('save')
	} catch (error) {
		saving.value = false
	}
}

defineExpose({
	open,
})

onBeforeUnmount(() => {
	resolveAction.value = null
})
</script>

<style scoped>
.unsaved-dialog {
	position: fixed;
	inset: 0;
	z-index: 10000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
}

.unsaved-dialog__backdrop {
	position: absolute;
	inset: 0;
	background: rgba(15, 23, 42, 0.45);
}

.unsaved-dialog__card {
	position: relative;
	width: 100%;
	max-width: 500px;
	background: #fff;
	border-radius: 14px;
	border: 1px solid #e2e8f0;
	box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
	padding: 16px;
}

.unsaved-dialog__title {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 18px;
	font-weight: 700;
	color: #0f172a;
	margin-bottom: 12px;
}

.unsaved-dialog__title-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 999px;
	background: #fef3c7;
	color: #92400e;
	font-weight: 700;
}

.unsaved-dialog__content {
	font-size: 14px;
	color: #334155;
}

.unsaved-dialog__message {
	margin: 0 0 12px;
}

.unsaved-dialog__hint {
	background: #eff6ff;
	border: 1px solid #bfdbfe;
	color: #1d4ed8;
	border-radius: 10px;
	padding: 10px 12px;
	font-size: 13px;
}

.unsaved-dialog__actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 16px;
}

.unsaved-dialog__btn {
	border: 0;
	border-radius: 10px;
	padding: 8px 12px;
	font-weight: 600;
	cursor: pointer;
}

.unsaved-dialog__btn:disabled {
	opacity: 0.65;
	cursor: not-allowed;
}

.unsaved-dialog__btn--ghost {
	background: transparent;
	color: #475569;
}

.unsaved-dialog__btn--primary {
	background: #2563eb;
	color: #fff;
}

.unsaved-dialog__btn--danger {
	background: #dc2626;
	color: #fff;
}
</style>
